import { Box, Button, Grid, Group } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import CampaignFormInner from '../Campaigns/CampaignFormInner';
import CrossDomainCopy from '../CrossDomainCopy';
import Flow from '../Flow/Flow';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';

const WorkflowForm = ({ saveCampaign, deleteCampaign, ...rest }) => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);
  const whatsappAccount = useSelector((state) => state.whatsapp.account);
  const googleAccount = useSelector((state) => state.google.account);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [campaign, setCampaign] = useState(rest.campaign);

  const [error, setError] = useState();
  const [customFields, setCustomFields] = useState();
  const [emails, setEmails] = useState();
  const [campaigns, setCampaigns] = useState();
  const [pushNotifications, setPushNotifications] = useState();

  const [textMessages, setTextMessages] = useState();
  const [viberMessages, setViberMessages] = useState();
  const [pages, setPages] = useState();
  const [facebookAds, setFacebookAds] = useState();
  const [eventTags, setEventTags] = useState();
  const [eventActions, setEventActions] = useState();
  const [subscriptions, setSubscriptions] = useState();
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const flowDataRef = useRef({ nodes: campaign.flow.nodes, edges: campaign.flow.edges });
  const [segments, setSegments] = useState();
  const [whatsappMessages, setWhatsappMessages] = useState();
  const [surveys, setSurveys] = useState();
  const [remainingDailyQuotaWhatsappMessages, setRemainingDailyQuotaWhatsappMessages] = useState('Unlimited');
  const [webhooks, setWebhooks] = useState();

  const [googleAds, setGoogleAds] = useState();
  const [loading, setLoading] = useState(false);
  const [isGoogleNode, setIsGoogleNode] = useState();
  const [hasAssetMinimumErrors, setHasAssetMinimumErrors] = useState(false);
  const [hasUnconfiguredConditionNode, setHasUnconfiguredConditionNode] = useState(false);
  const { accountId, domainId } = useParams();
  const tabs = ['content', 'audience', 'launchConfiguration', 'goal'];
  const tab = search.tab || tabs[0];

  const setTab = (tab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, tab })}`);
  };

  if (isGoogleNode) {
    tabs.push('assetGroupUrlConfiguration');
  }

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails`)
      .then(({ emails }) => setEmails(emails))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns`)
      .then(({ campaigns }) => setCampaigns(campaigns))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pushNotifications`)
      .then(({ pushNotifications }) => setPushNotifications(pushNotifications))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/textMessages`)
      .then(({ textMessages }) => setTextMessages(textMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages`)
      .then(({ viberMessages }) => setViberMessages(viberMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebookAds`)
      .then(({ facebookAds }) => setFacebookAds(facebookAds))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventTags`)
      .then(({ eventTags }) => setEventTags(eventTags))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages`)
      .then(({ pages }) => setPages(pages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/subscriptions`)
      .then(({ subscriptions }) => setSubscriptions(subscriptions))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments`)
      .then(({ segments }) => setSegments(segments))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => {
        setEventActions(eventActions);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => {
        setSurveys(surveys);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/webhooks`)
      .then(({ webhooks }) => {
        setWebhooks(webhooks);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  const loadGoogleAds = useCallback(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds`)
      .then(({ googleAds }) => {
        const filteredGoogleAds = googleAds.filter((ad) => {
          return googleAccount?.adAccountId && ad.resourceName?.includes(googleAccount.adAccountId);
        });
        setGoogleAds(filteredGoogleAds);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, googleAccount?.adAccountId]);

  useEffect(() => {
    loadGoogleAds();
  }, [loadGoogleAds]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsappMessages`)
      .then(({ whatsappMessages }) => {
        const now = Date.now();
        const oneHourAgo = now - 60 * 60 * 1000;
        const filteredMessages = whatsappMessages.filter((m) => {
          const updatedAtTime = new Date(m.updatedAt).getTime();
          const isOlderThanOneHour = updatedAtTime < oneHourAgo;
          const matchesSender = whatsappAccount?.whatsappSender ? m.stored === whatsappAccount?.whatsappSender : true;

          return isOlderThanOneHour && matchesSender;
        });
        setWhatsappMessages(filteredMessages);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, whatsappAccount?.whatsappSender]);

  useEffect(() => {
    if (
      (whatsappAccount?.whatsappSender === 'bsp' &&
        whatsappAccount?.bspAuthToken &&
        whatsappAccount?.whatsappAccountId) ||
      (whatsappAccount?.whatsappAccountId && whatsappAccount?.whatsappSender !== 'bsp')
    ) {
      fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/whatsapp/${whatsappAccount?.whatsappAccountId}/remainingDailyQuotaWhatsappMessages`
      )
        .then(({ response }) => setRemainingDailyQuotaWhatsappMessages(response))
        .catch(setError);
    }
  }, [
    accountId,
    domainId,
    fetch,
    whatsappAccount?.whatsappSender,
    whatsappAccount?.whatsappAccountId,
    whatsappAccount?.bspAuthToken,
  ]);

  const handleChange = (e) => {
    setCampaign((prevCampaign) => {
      let change = {};
      let changeInner = change;
      let campaignInner = { ...prevCampaign };

      const nameSplit = e.target.name.split('.');
      let i = 0;
      let key;
      for (;;) {
        key = nameSplit[i];
        i++;

        if (i === nameSplit.length) {
          changeInner[key] = e.target.value;
          break;
        } else {
          changeInner[key] = { ...campaignInner[key] };
          changeInner = changeInner[key];
          campaignInner = campaignInner[key] || {};
        }
      }

      return { ...prevCampaign, ...change };
    });
  };

  if (error) {
    return `An error has occurred: ${error.message}`;
  }

  if (
    campaign === undefined ||
    emails === undefined ||
    pushNotifications === undefined ||
    customFields === undefined ||
    eventTags === undefined ||
    eventActions === undefined ||
    textMessages === undefined ||
    viberMessages === undefined ||
    pages === undefined ||
    segments === undefined ||
    whatsappMessages === undefined ||
    surveys === undefined ||
    googleAds === undefined
  ) {
    return '';
  }

  const isCampaignEditLocked =
    (campaign.mode !== 'oneOff' && campaign.running) ||
    (campaign.mode === 'oneOff' && (campaign.running || campaign.finishedAt || campaign.aborted)) ||
    campaign.googleAssetGroup?.campaignResourceName;

  let deleteButton;
  if (deleteCampaign) {
    deleteButton = (
      <LockableButton
        restrictionKey={`${campaign.mode}WorkflowsDelete`}
        color="red"
        onClick={() => setConfirmDeleteOpen(true)}
      >
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  return (
    <Box>
      <CampaignFormInner
        tab={tab}
        setTab={setTab}
        campaign={campaign}
        handleChange={(e) => handleChange(e)}
        contentNameCol={3}
        segmentScope="behavior"
        mainContent={
          <>
            <hr />
            <Grid data-tour="flow">
              <Flow
                setIsGoogleNode={setIsGoogleNode}
                setHasUnconfiguredConditionNode={setHasUnconfiguredConditionNode}
                reactFlowInstance={reactFlowInstance}
                setReactFlowInstance={setReactFlowInstance}
                flowDataRef={flowDataRef}
                campaignMode={campaign.mode}
                nodes={campaign.flow.nodes}
                edges={campaign.flow.edges}
                campaigns={campaigns}
                emails={emails}
                pushNotifications={pushNotifications}
                textMessages={textMessages}
                viberMessages={viberMessages}
                facebookAds={facebookAds}
                availableCustomFields={customFields}
                eventTags={eventTags}
                eventActions={eventActions}
                readOnly={campaign.running}
                pages={pages}
                subscriptions={subscriptions}
                segments={segments}
                whatsappMessages={whatsappMessages}
                surveys={surveys}
                remainingDailyQuotaWhatsappMessages={remainingDailyQuotaWhatsappMessages}
                googleAds={googleAds}
                webhooks={webhooks}
              />
            </Grid>
          </>
        }
        isCampaignEditLocked={isCampaignEditLocked}
        googleNodeConnected={isGoogleNode}
        onAssetValidationChange={setHasAssetMinimumErrors}
      />

      <Group justify="flex-end" gap="xs" py="md">
        <Button
          variant="default"
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(-1);
          }}
        >
          {' '}
          <IconChevronLeft /> Back
        </Button>
        {deleteButton}
        {campaign.id && (
          <CrossDomainCopy
            resourceType="Workflow"
            resourceId={campaign.id}
            basePath="workflows"
            restrictionKey={`${campaign.mode}WorkflowsCreate`}
            fallbackAction={() => {
              navigate(
                location.pathname.replace(`/workflows/${campaign.id}/details`, `/workflows/new?from=${campaign.id}`)
              );
            }}
          />
        )}
        {campaign.id || tabs.indexOf(tab) === tabs.length - 1 ? (
          <LockableButton
            data-tour="workflowsSaveBtn"
            restrictionKey={`${campaign.mode}Workflows${campaign.id ? 'Update' : 'Create'}`}
            onClick={() => {
              setLoading(true);
              saveCampaign({
                ...campaign,
                flow: {
                  ...campaign.flow,
                  nodes: flowDataRef.current.nodes,
                  edges: flowDataRef.current.edges,
                },
              }).finally(() => setLoading(false));
            }}
            disabled={
              !campaign.name ||
              campaign.flow.length === 0 ||
              campaign.mode === 'custom' ||
              (campaign.mode === 'triggered' && !campaign.trigger) ||
              (!(campaign.mode === 'triggered' && campaign.trigger?.type === 'event') && !campaign.segmentId) ||
              (campaign.googleAssetGroup?.pathOne === '' && campaign.googleAssetGroup?.pathTwo !== '') ||
              campaign.googleAssetGroup?.pathOne?.length > 15 ||
              campaign.googleAssetGroup?.pathTwo?.length > 15 ||
              (campaign.mode === 'continuous' && campaign.reentryCooldownInDays < 1) ||
              (isGoogleNode && hasAssetMinimumErrors) ||
              hasUnconfiguredConditionNode ||
              loading
            }
          >
            <IconDeviceFloppy size={14} /> {loading ? 'Saving...' : 'Save'}
          </LockableButton>
        ) : (
          <Button
            onClick={() => {
              setTab(tabs[tabs.indexOf(tab) + 1]);
              window.scrollTo(0, 0);
            }}
            data-tour="nextButtonWorkflows"
          >
            Next <IconChevronRight size={14} />
          </Button>
        )}
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteCampaign(cb)}
          message="This action cannot be undone!"
        />
      </Group>
    </Box>
  );
};

export default WorkflowForm;
